/* Only the selected viewport video is mounted. Muted inline autoplay, no controls.
 *
 * caene-hero-video is display:contents (assets/caene-mugler-hero.css, scoped to
 * this element only) — it has no layout box of its own, so it must never be the
 * IntersectionObserver target: a display:contents element's intersection is
 * unreliable across browsers. We observe its parent .caene-mugler-hero instead,
 * which is the real, positioned box the hero actually occupies.
 *
 * Playback is driven entirely by canPlay(): visible (intersection ratio > 0),
 * document visible, reduced motion off, and the current, connected video.
 * Native autoplay is off, so a page restored mid-scroll (hero offscreen) never
 * starts the video on its own — only the observer's first callback can. Every
 * path that could start or keep playback going — sync(), and the 'playing'
 * event itself — is gated on the same check, so a late event can never leave
 * an offscreen/ineligible video running.
 */
if (!customElements.get('caene-hero-video')) {
  customElements.define('caene-hero-video', class extends HTMLElement {
    connectedCallback() {
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.posterReady = false;
      const cycle = {};
      this.cycle = cycle;

      /* Re-derived at the moment it matters, never cached: covers sync() calls
         and the 'playing' handler alike, so a stale event is judged against
         current eligibility, not the eligibility at the time it was queued. */
      const canPlay = (video) =>
        video === this.video &&
        video.isConnected &&
        this.visible &&
        this.posterReady &&
        !this.reducedMotion.matches &&
        !document.hidden;

      this.selectVideo = () => {
        this.video?.pause();
        this.video?.remove();
        this.video = null;
        const desktop = this.querySelector('[data-hero-video-desktop]');
        const source = this.mobile.matches ? this.querySelector('[data-hero-video-mobile]') || desktop : desktop;
        if (!source) return;
        const video = source.content.querySelector('video').cloneNode(true);
        this.video = video;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.controls = false;
        video.loop = true;
        /* No native autoplay: sync() below is the only thing allowed to start
           playback, and only once the observer has confirmed visibility. */
        video.autoplay = false;
        video.addEventListener('playing', () => {
          /* A 'playing' event only confirms the browser started decoding — it
             does not mean playback is still wanted. Re-check eligibility right
             now: if anything has since made this video ineligible (scrolled
             offscreen, tab hidden, reduced motion, replaced, disconnected),
             pause it instead of letting the event mark it as playing. */
          if (canPlay(video)) {
            video.classList.add('is-playing');
          } else {
            video.pause();
          }
        });
        video.addEventListener('error', () => {
          if (video !== this.video) return;
          video.pause();
          video.classList.remove('is-playing');
        });
        source.parentNode.append(video);
        this.sync();
      };

      this.sync = () => {
        const video = this.video;
        if (!video) return;
        if (canPlay(video)) {
          video.play().catch(() => {
            /* Rejected play (autoplay policy, interrupted, etc): leave the
               permanent poster visible rather than a black frame. */
            if (video !== this.video) return;
            video.classList.remove('is-playing');
          });
        } else {
          video.pause();
        }
      };

      /* threshold 0, gated on a positive ratio: a hero taller than the
         viewport can sit at a low ratio indefinitely, so a higher fixed
         threshold (e.g. 0.5) may never fire. isIntersecting plus ratio > 0 is
         what actually reports "any part of the hero is on screen". */
      const observer = new IntersectionObserver((entries) => {
        /* Stale guard across disconnect/reconnect: disconnectedCallback nulls
           this.observer, and a fresh connectedCallback assigns a new one, so
           a callback queued by this exact instance before it was torn down
           (or superseded) is identified here and dropped rather than acting
           on a state that no longer belongs to the live cycle. */
        if (observer !== this.observer) return;
        const entry = entries[0];
        this.visible = entry.isIntersecting && entry.intersectionRatio > 0;
        this.sync();
      }, { threshold: 0 });
      this.observer = observer;
      const box = this.closest('.caene-mugler-hero');
      if (box) this.observer.observe(box);

      /* Reduced-motion changes only pause/resume the existing element — never
         selectVideo(), which would recreate the node and reset playback. Only
         a viewport breakpoint change is allowed to swap the source. */
      this.onMotionChange = () => this.sync();
      this.onVisibilityChange = () => this.sync();

      this.mobile.addEventListener('change', this.selectVideo);
      this.reducedMotion.addEventListener('change', this.onMotionChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.selectVideo();
      // Let the responsive poster decode and paint before video competes for bandwidth.
      const poster = this.querySelector('.caene-mugler-hero__video-poster img');
      const ready = poster?.decode ? poster.decode().catch(() => {}) : Promise.resolve();
      ready.then(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.cycle !== cycle || !this.isConnected) return;
        this.posterReady = true;
        this.sync();
      })));
    }
    disconnectedCallback() {
      this.cycle = null;
      this.observer?.disconnect();
      this.observer = null;
      this.mobile?.removeEventListener('change', this.selectVideo);
      this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.video?.pause();
      this.video?.remove();
      /* Nulled so any play()/playing/error callback still pending from before
         disconnect sees a mismatch (canPlay's video === this.video check) and
         does nothing. */
      this.video = null;
    }
  });
}
